import { ArrowRight, Check, TrendingUp, X } from "lucide-react";

import Eyebrow from "../../Eyebrow";
import Reveal from "../../Reveal";
import SectionHeading from "../../SectionHeading";
import TechNote from "../TechNote";
import { howItWorksSections } from "../../../../content/howItWorksSections";

/**
 * The Problem → Solution → Result row template, reused by each concrete
 * capability below — same icons, same layout, only the copy changes.
 * Translation: The Problem → Solution → Result row template, reused by each concrete
 * capability below — same icons, same layout, only the copy changes.
 *
 * @param {Object} props - Flow row props.
 * @param {{title: string, description: string}} props.problem - Problem step copy.
 * @param {{title: string, description: string}} props.solution - Solution step copy.
 * @param {{title: string, description: React.ReactNode}} props.result - Result step copy.
 * @returns {JSX.Element} Problem/Solution/Result flow row.
 */
const CapabilityFlow = ({ problem, solution, result }) => (
  <div className="hiw-capabilities-legend">
    <div className="hiw-capabilities-legend__step">
      <X className="hiw-capability__mark hiw-capability__mark--no" strokeWidth={2} aria-hidden="true" />
      <div>
        <strong>{problem.title}</strong>
        <span>{problem.description}</span>
      </div>
    </div>
    <ArrowRight className="hiw-capabilities-legend__arrow" strokeWidth={1.2} aria-hidden="true" />
    <div className="hiw-capabilities-legend__step">
      <Check className="hiw-capability__mark hiw-capability__mark--yes" strokeWidth={2} aria-hidden="true" />
      <div>
        <strong>{solution.title}</strong>
        <span>{solution.description}</span>
      </div>
    </div>
    <ArrowRight className="hiw-capabilities-legend__arrow" strokeWidth={1.2} aria-hidden="true" />
    <div className="hiw-capabilities-legend__step">
      <TrendingUp className="hiw-capability__mark hiw-capabilities-legend__mark--result" strokeWidth={2} aria-hidden="true" />
      <div>
        <strong>{result.title}</strong>
        <span>{result.description}</span>
      </div>
    </div>
  </div>
);

/**
 * Problem / solution / result blocks for each of the four managed capabilities.
 * Translation: Problem / solution / result blocks for each of the four managed capabilities.
 *
 * @returns {JSX.Element} Capabilities section.
 */
const Capabilities = () => {
  const { capabilities, techNotes } = howItWorksSections;

  return (
    <section id="capabilities" className="section-block">
      <div className="section-shell">
        <SectionHeading eyebrow={capabilities.eyebrow} title={capabilities.title} />

        {capabilities.items.map((item) => (
          <Reveal key={item.title} className="hiw-capability-block">
            <Eyebrow>
              {item.label}
              <TechNote>{techNotes[item.techNote]}</TechNote>
            </Eyebrow>
            <h3 className="hiw-capability__title">{item.title}</h3>

            <CapabilityFlow
              problem={{ title: "Problem", description: item.problem }}
              solution={{ title: "Solution", description: item.solution }}
              result={{
                title: "Result",
                description: (
                  <>
                    <span className="hiw-capability-flow__stat">{item.stat.big}</span>
                    {" — "}
                    {item.stat.caption}
                  </>
                ),
              }}
            />

            {item.detail && <p className="hiw-detail-text hiw-capability-flow__detail">{item.detail}</p>}
          </Reveal>
        ))}
      </div>
    </section>
  );
};

export default Capabilities;
